import type { FC } from "react";
import type { ProductHistoricalInsights } from "../historical-insights.types";
import { formatCurrency, formatDays, formatPercent } from "../insights-utils";

interface Props {
  bound: ProductHistoricalInsights["bound"];
}

export const InsightsBoundSection: FC<Props> = ({ bound }) => {
  if (bound.totalCount === 0) {
    return null;
  }

  const classEntries = Object.entries(bound.byClass);
  const stateEntries = Object.entries(bound.byState);
  const hasDetailData = classEntries.length > 0 || stateEntries.length > 0 || bound.topCombinations.length > 0;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Bound Deals ({bound.totalCount})</h3>

      <div className="flex gap-4 text-xs text-muted-foreground">
        {bound.overallBindRate != null && <span>Bind rate: {formatPercent(bound.overallBindRate)}</span>}
        {bound.avgDaysToQuoteToBind != null && <span>Avg quote-to-bind: {formatDays(bound.avgDaysToQuoteToBind)}</span>}
      </div>

      {!hasDetailData && <p className="text-xs text-muted-foreground">More data needed for detailed breakdown.</p>}

      {classEntries.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">By Class</h4>
          <div className="space-y-1">
            {classEntries.map(([cls, data]) => (
              <div key={cls} className="flex justify-between text-xs">
                <span>{cls}</span>
                <span className="text-muted-foreground">
                  {data.count} · {formatCurrency(data.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stateEntries.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">By State</h4>
          <div className="space-y-1">
            {stateEntries.map(([state, data]) => (
              <div key={state} className="flex justify-between text-xs">
                <span>{state}</span>
                <span className="text-muted-foreground">
                  {data.count} · {formatCurrency(data.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {bound.topCombinations.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Combinations</h4>
          <div className="space-y-1">
            {bound.topCombinations.map((combo, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>
                  {combo.class} / {combo.state}
                </span>
                <span className="text-muted-foreground">
                  {combo.count} · {formatCurrency(combo.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
